import AddonVariant from "src/api/graphql/__generated__/enums/AddonVariant";
import { PaletteFragmentFragment } from "src/api/graphql/__generated__/graphql";
import native from "src/constants/native";
import { GatheringContextType } from "src/contexts/GatheringContext/GatheringContext";
import GatheringCommentsThreadSkeleton from "src/screens/(internal)/(Comments)/GatheringCommentsThread/GatheringCommentsThreadSkeleton";
import PhotoCommentsThreadSkeleton from "src/screens/(internal)/(Comments)/PhotoCommentsThread/PhotoCommentsThreadSkeleton";
import GatheringAdminPrivacyModalSkeleton from "src/screens/(internal)/(GatheringHome)/(GatheringPrivacyModals)/GatheringAdminPrivacyModal/GatheringAdminPrivacyModalSkeleton";
import GatheringParticipantPrivacyModalSkeleton from "src/screens/(internal)/(GatheringHome)/(GatheringPrivacyModals)/GatheringParticipantPrivacyModal/GatheringParticipantPrivacySkeleton";
import GatheringAddonEditModalSkeleton from "src/screens/(internal)/(GatheringHome)/GatheringAddonEditModal/GatheringAddonEditModalSkeleton";
import GatheringFilterSortModalSkeleton from "src/screens/(internal)/(GatheringHome)/GatheringFilterSortModal/GatheringFilterSortModalSkeleton";
import { GatheringFilterSortModalProps } from "src/screens/(internal)/(GatheringHome)/GatheringFilterSortModal/types";
import GatheringThemeModalSkeleton from "src/screens/(internal)/(GatheringHome)/GatheringThemeModal/GatheringThemeModalSkeleton";
import GatheringViewersModalSkeleton from "src/screens/(internal)/(GatheringHome)/GatheringViewersModal/GatheringViewersModalSkeleton";
import PhotoDetailModalSkeleton from "src/screens/(internal)/PhotoDetailModal/PhotoDetailModalSkeleton";
import PhotoReactionModalSkeleton from "src/screens/(internal)/PhotoReactionModal/PhotoReactionModalSkeleton";
import lazy from "src/swsh-native/lazy";
import { ModalConfig } from "../types";

const GatheringViewersModal = lazy(
	() => import("src/screens/(internal)/(GatheringHome)/GatheringViewersModal"),
	{
		loading: GatheringViewersModalSkeleton,
	},
);
const GatheringAdminPrivacyModal = lazy(
	() =>
		import(
			"src/screens/(internal)/(GatheringHome)/(GatheringPrivacyModals)/GatheringAdminPrivacyModal"
		),
	{
		loading: GatheringAdminPrivacyModalSkeleton,
	},
);
const GatheringParticipantPrivacyModal = lazy(
	() =>
		import(
			"src/screens/(internal)/(GatheringHome)/(GatheringPrivacyModals)/GatheringParticipantPrivacyModal"
		),
	{
		loading: GatheringParticipantPrivacyModalSkeleton,
	},
);
const GatheringThemeModal = lazy(
	() => import("src/screens/(internal)/(GatheringHome)/GatheringThemeModal/GatheringThemeModal"),
	{
		loading: GatheringThemeModalSkeleton,
	},
);

const PhotoDetailModal = lazy(() => import("src/screens/(internal)/PhotoDetailModal"), {
	loading: PhotoDetailModalSkeleton,
});
const PhotoReactionModal = lazy(() => import("src/screens/(internal)/PhotoReactionModal"), {
	loading: PhotoReactionModalSkeleton,
});
const GatheringAddonEditModal = lazy(
	() => import("src/screens/(internal)/(GatheringHome)/GatheringAddonEditModal"),
	{
		loading: GatheringAddonEditModalSkeleton,
	},
);
const GatheringFilterSortModal = lazy(
	() => import("src/screens/(internal)/(GatheringHome)/GatheringFilterSortModal"),
	{
		loading: GatheringFilterSortModalSkeleton,
	},
);
const PhotoCommentsThread = lazy(
	() => import("src/screens/(internal)/(Comments)/PhotoCommentsThread"),
	{
		loading: PhotoCommentsThreadSkeleton,
	},
);
const GatheringCommentsThread = lazy(
	() => import("src/screens/(internal)/(Comments)/GatheringCommentsThread"),
	{
		loading: GatheringCommentsThreadSkeleton,
	},
);

type GatheringModal = {
	GatheringViewers: {
		props: {
			context: GatheringContextType;
			gatheringId: string;
		};
	};
	GatheringAdminPrivacy: {
		props: {
			gatheringId: string;
		};
	};
	GatheringParticipantPrivacy: {
		props: {
			gatheringId: string;
		};
	};
	PhotoDetailModal: {
		props: {
			context: GatheringContextType;
			initialPhotoId: string;
		};
	};
	GatheringThemeModal: {
		props: {
			gatheringId: string;
			defaultPalette?: PaletteFragmentFragment;
		};
	};
	PhotoReactionModal: {
		props: {
			photoId: string;
			gatheringId: string;
			numPrimaryReaction?: number;
		};
	};
	GatheringAddonEditModal: {
		props: {
			gatheringId: string;
			addonId?: string;
			variant: AddonVariant;
			url?: string;
			label?: string;
		};
	};
	GatheringFilterSortModal: {
		props: GatheringFilterSortModalProps;
	};
	PhotoCommentsThread: {
		props: {
			photoId: string;
			gatheringId: string;
			isAdmin: boolean;
		};
	};
	GatheringCommentsThread: {
		props: {
			gatheringId: string;
			isAdmin: boolean;
		};
	};
};

export default GatheringModal;

export const GatheringModalConfig = {
	GatheringViewers: {
		component: GatheringViewersModal,
		variant: "Tall",
	},
	GatheringAdminPrivacy: {
		component: GatheringAdminPrivacyModal,
		variant: "Full",
	},
	GatheringParticipantPrivacy: {
		component: GatheringParticipantPrivacyModal,
		variant: "Full",
	},
	PhotoDetailModal: {
		component: PhotoDetailModal,
		variant: native ? "Full" : "FullBlur",
		overrideNativeScreenOptions: {
			presentation: "card",
			animation: "fade",
			gestureEnabled: false,
			fullScreenGestureEnabled: false,
			animationDuration: 200,
		},
	},
	GatheringThemeModal: {
		component: GatheringThemeModal,
		variant: "Full",
	},
	PhotoReactionModal: {
		component: PhotoReactionModal,
		variant: native ? "Full" : "ShortBlur",
	},
	GatheringAddonEditModal: {
		component: GatheringAddonEditModal,
		variant: "Tall",
	},
	GatheringFilterSortModal: {
		component: GatheringFilterSortModal,
		variant: "Tall",
	},
	PhotoCommentsThread: {
		component: PhotoCommentsThread,
		variant: native ? "FullBlur" : "Full",
	},
	GatheringCommentsThread: {
		component: GatheringCommentsThread,
		variant: "Full",
	},
} satisfies ModalConfig<GatheringModal>;
